import SEO from "@/components/SEO";
import Breadcrumb from "@/components/Breadcrumb";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Phone, Shield, Users, Plane, Car, Heart, CheckCircle2, Quote, HelpCircle, MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import InstantQuoteCTA from "@/components/InstantQuoteCTA";
import funeralHeroImg from "@/assets/funeral-hero.webp";
import audiLuxuryImg from "@/assets/audi-luxury.jpg";

const FuneralTransportation = () => {
  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Funeral Transportation",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Rick's Black Car Service",
      "image": "https://ricksblackcarservicesantonio.com/images/audi-q7-black.jpg",
      "telephone": "+1-210-296-7409",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "16034 Rough Oak Street",
        "addressLocality": "San Antonio",
        "addressRegion": "TX",
        "postalCode": "78232",
        "addressCountry": "US"
      },
      "priceRange": "$$$"
    },
    "areaServed": [
      {
        "@type": "City",
        "name": "San Antonio",
        "sameAs": "https://en.wikipedia.org/wiki/San_Antonio"
      },
      {
        "@type": "Cemetery",
        "name": "Fort Sam Houston National Cemetery",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "1520 Harry Wurzbach Rd",
          "addressLocality": "San Antonio",
          "addressRegion": "TX",
          "postalCode": "78209"
        }
      }
    ],
    "description": "Dignified private black car service for funeral processions and family transport in San Antonio. Authorized JBSA access for Fort Sam Houston Cemetery.",
    "offers": {
      "@type": "Offer",
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock"
    }
  };

  const funeralHomesSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Funeral Homes Served by Rick's Black Car Service",
    "description": "San Antonio funeral homes and cemeteries we provide dignified transportation services to",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "item": {
          "@type": "FuneralHome",
          "name": "Mission Park Funeral Chapels",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "San Antonio",
            "addressRegion": "TX"
          },
          "areaServed": "San Antonio"
        }
      },
      {
        "@type": "ListItem",
        "position": 2,
        "item": {
          "@type": "FuneralHome",
          "name": "Porter Loring Mortuary",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "1101 McCullough Ave",
            "addressLocality": "San Antonio",
            "addressRegion": "TX",
            "postalCode": "78212"
          }
        }
      },
      {
        "@type": "ListItem",
        "position": 3,
        "item": {
          "@type": "Cemetery",
          "name": "Sunset Memorial Park",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "1701 Austin Hwy",
            "addressLocality": "San Antonio",
            "addressRegion": "TX",
            "postalCode": "78218"
          }
        }
      },
      {
        "@type": "ListItem",
        "position": 4,
        "item": {
          "@type": "Cemetery",
          "name": "San Fernando Cemetery",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "San Antonio",
            "addressRegion": "TX"
          }
        }
      },
      {
        "@type": "ListItem",
        "position": 5,
        "item": {
          "@type": "Cemetery",
          "name": "Fort Sam Houston National Cemetery",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "1520 Harry Wurzbach Rd",
            "addressLocality": "San Antonio",
            "addressRegion": "TX",
            "postalCode": "78209"
          },
          "description": "National cemetery requiring JBSA military base access"
        }
      },
      {
        "@type": "ListItem",
        "position": 6,
        "item": {
          "@type": "FuneralHome",
          "name": "Alamo Funeral Home",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "San Antonio",
            "addressRegion": "TX"
          }
        }
      },
      {
        "@type": "ListItem",
        "position": 7,
        "item": {
          "@type": "FuneralHome",
          "name": "Heritage Funeral Home",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Castle Hills",
            "addressRegion": "TX"
          }
        }
      },
      {
        "@type": "ListItem",
        "position": 8,
        "item": {
          "@type": "FuneralHome",
          "name": "Hillcrest Funeral Home",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "San Antonio",
            "addressRegion": "TX"
          }
        }
      },
      {
        "@type": "ListItem",
        "position": 9,
        "item": {
          "@type": "FuneralHome",
          "name": "Funeral Caring USA",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "San Antonio",
            "addressRegion": "TX"
          }
        }
      }
    ]
  };

  const faqItems = [
    {
      question: "How soon can you provide funeral transportation services?",
      answer: "We understand that bereavement situations often arise unexpectedly. We can typically accommodate same-day or next-day requests, depending on availability. Please call us directly at 210-296-7409 for urgent arrangements, and we will do everything possible to assist your family."
    },
    {
      question: "Can you transport our family to Fort Sam Houston National Cemetery?",
      answer: "Yes. As an authorized commercial carrier with verified JBSA military base access, our chauffeur and vehicle are pre-cleared for unescorted entry to Fort Sam Houston National Cemetery. This means your family arrives without delays or complications at the gate—a service most transportation providers cannot offer."
    },
    {
      question: "How many passengers can your vehicle accommodate?",
      answer: "Our 2025 Audi Q7 Black Optic SUV comfortably seats up to 6 passengers. The spacious interior also accommodates mobility aids such as walkers or wheelchairs. For larger family groups, we can coordinate multiple vehicles."
    },
    {
      question: "Do you provide airport pickup for out-of-town relatives?",
      answer: "Absolutely. We offer personalized meet-and-greet service at San Antonio International Airport (SAT) for family members flying in for the service. We coordinate pickup times with flight arrivals and can transport relatives directly to the funeral home, hotel, or family residence."
    },
    {
      question: "What is your pricing for funeral transportation?",
      answer: "We offer compassionate, transparent pricing tailored to your family's needs. Rates depend on the services required—whether airport pickups, procession driving, or multi-day support. Please contact us for a personalized quote. We never add hidden fees during such a difficult time."
    },
    {
      question: "Can you drive in the funeral procession?",
      answer: "Yes. Our professional chauffeur is experienced in funeral cortege driving, maintaining proper pace and positioning throughout the procession. We coordinate with funeral directors to ensure a dignified, seamless journey to the cemetery or memorial site."
    },
    {
      question: "Why choose a private SUV over a traditional limousine?",
      answer: "Many families prefer the understated elegance of our luxury Audi Q7 over a stretch limousine. It offers privacy, comfort, and a low-profile presence that feels more personal and less conspicuous. The tinted privacy glass and whisper-quiet cabin provide a peaceful space for reflection."
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqItems.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  };

  const pricingSchema = {
    "@context": "https://schema.org",
    "@type": "PriceSpecification",
    "name": "Funeral Transportation Pricing",
    "description": "Transparent pricing for luxury funeral car service in San Antonio.",
    "priceCurrency": "USD",
    "minPrice": "115.00",
    "price": "135.00",
    "unitText": "per hour",
    "valueAddedTaxIncluded": false
  };

  const services = [
    {
      icon: Users,
      title: "Immediate Family Transport",
      description: "Door-to-door private service for the spouse, parents, and immediate family. We ensure your loved ones travel together in comfort and privacy during this difficult time."
    },
    {
      icon: Plane,
      title: "Airport Logistics",
      description: "Picking up out-of-town relatives from SAT Airport with personalized meet-and-greet service. We handle the logistics so your family can focus on being together."
    },
    {
      icon: Car,
      title: "Procession Service",
      description: "Professional, respectful driving in the funeral cortege. Our chauffeur maintains proper pace and positioning, ensuring a dignified procession to the final resting place."
    }
  ];

  return (
    <>
      <SEO 
        title="Funeral Transportation San Antonio | Dignified Family Car Service"
        description="Private bereavement transportation in San Antonio. Respectful chauffeur service for funerals, memorials, and processions. Authorized JBSA access for Fort Sam Houston National Cemetery."
        keywords="funeral transportation san antonio, bereavement car service, funeral home transportation, memorial service transportation, fort sam houston national cemetery transportation, private family transport funeral"
        canonicalPath="/services/funeral-transportation"
        schema={[schemaMarkup, faqSchema, funeralHomesSchema, pricingSchema]}
      />

      <div className="min-h-screen bg-background">
        <Navbar />
        <main className="pt-20">
          <Breadcrumb 
            items={[
              { name: "Services", url: "/services" },
              { name: "Funeral Transportation", url: "/services/funeral-transportation" }
            ]}
          />

          {/* Hero Section - Serene & Dignified */}
          <section className="relative min-h-[70vh] overflow-hidden flex items-center justify-center bg-black">
            <div className="absolute inset-0 z-0">
              <img 
                src={funeralHeroImg} 
                alt="Dignified funeral transportation San Antonio" 
                className="w-full h-full object-cover opacity-60 animate-[fade-in_1.5s_ease-out]"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/30 to-black/60" />
            </div>
            <div className="container mx-auto px-4 relative z-10 text-center py-16">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-white mb-6 leading-tight animate-fade-in" style={{ animationDelay: '0.3s', animationFillMode: 'both' }}>
                Dignified, Private Family Transportation
              </h1>
              <p className="text-xl md:text-2xl text-gray-300 mb-10 max-w-3xl mx-auto leading-relaxed animate-fade-in" style={{ animationDelay: '0.6s', animationFillMode: 'both' }}>
                Respectful chauffeur service for funerals, memorials, and processions in San Antonio.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in" style={{ animationDelay: '0.9s', animationFillMode: 'both' }}>
                <Button 
                  className="bg-primary hover:bg-primary/90 text-primary-foreground text-lg px-8 py-6 font-semibold shadow-lg transition-transform duration-300 hover:scale-105" 
                  asChild
                >
                  <Link to="/contact">
                    <Heart className="w-5 h-5 mr-2" />
                    Request a Bereavement Quote
                  </Link>
                </Button>
                <Button 
                  variant="outline"
                  className="border-white/30 text-white hover:bg-white/10 text-lg px-8 py-6 transition-transform duration-300 hover:scale-105"
                  asChild
                >
                  <a href="tel:+12102967409">
                    <Phone className="w-5 h-5 mr-2" />
                    Call for Immediate Assistance
                  </a>
                </Button>
              </div>
            </div>
          </section>

          {/* JBSA Advantage Section - Critical */}
          <section className="py-16 bg-gradient-to-b from-black to-background">
            <div className="container mx-auto px-4">
              <Card className="max-w-4xl mx-auto border-primary/30 bg-card/50 backdrop-blur animate-fade-in" style={{ animationDelay: '0.2s', animationFillMode: 'both' }}>
                <CardContent className="p-8 md:p-12">
                  <div className="flex flex-col md:flex-row items-center gap-8">
                    <div className="flex-shrink-0">
                      <div className="w-24 h-24 rounded-full bg-primary/20 flex items-center justify-center border-2 border-primary">
                        <Shield className="w-12 h-12 text-primary" />
                      </div>
                    </div>
                    <div className="text-center md:text-left">
                      <div className="inline-block px-4 py-1 bg-primary/20 rounded-full text-primary text-sm font-semibold mb-4">
                        AUTHORIZED ACCESS
                      </div>
                      <h2 className="text-2xl md:text-3xl font-serif font-bold text-foreground mb-4">
                        Fort Sam Houston National Cemetery Access
                      </h2>
                      <p className="text-muted-foreground text-lg leading-relaxed">
                        As an authorized commercial carrier with <strong className="text-foreground">verified JBSA military base access</strong>, 
                        we provide direct gate-to-graveside transportation at Fort Sam Houston National Cemetery. 
                        Unlike rideshare services, our chauffeur and vehicle are pre-cleared for unescorted base entry—ensuring 
                        your family arrives with dignity and without delay.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </section>

          {/* Vehicle Showcase */}
          <section className="py-20 bg-background">
            <div className="container mx-auto px-4">
              <div className="max-w-6xl mx-auto">
                <div className="grid lg:grid-cols-2 gap-12 items-center">
                  <div className="order-2 lg:order-1 animate-fade-in" style={{ animationDelay: '0.2s', animationFillMode: 'both' }}>
                    <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-6">
                      The Modern, Understated Alternative to a Limousine
                    </h2>
                    <p className="text-muted-foreground text-lg leading-relaxed mb-8">
                      Our 2025 Audi Q7 Black Optic SUV offers families a refined, private experience 
                      without the spectacle of a stretch limousine. Elegant simplicity during a time 
                      when privacy matters most.
                    </p>
                    <ul className="space-y-4">
                      {[
                        "Tinted privacy glass for family discretion",
                        "Whisper-quiet cabin for peaceful reflection",
                        "Tri-zone climate control for passenger comfort",
                        "Premium leather seating for up to 6 passengers",
                        "Spacious interior accommodates mobility aids",
                        "Professional black-on-black exterior presentation"
                      ].map((feature, index) => (
                        <li key={index} className="flex items-start gap-3 text-muted-foreground">
                          <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="order-1 lg:order-2 animate-fade-in" style={{ animationDelay: '0.4s', animationFillMode: 'both' }}>
                    <div className="relative">
                      <img 
                        src={audiLuxuryImg} 
                        alt="2025 Audi Q7 Black Optic - funeral transportation vehicle" 
                        className="w-full rounded-lg shadow-2xl"
                      />
                      <div className="absolute -bottom-4 -right-4 bg-primary text-primary-foreground px-6 py-3 rounded-lg shadow-lg">
                        <span className="font-semibold">2025 Audi Q7 Black Optic</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Services Grid */}
          <section className="py-20 bg-muted/30">
            <div className="container mx-auto px-4">
              <div className="text-center mb-12 animate-fade-in" style={{ animationDelay: '0.2s', animationFillMode: 'both' }}>
                <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-4">
                  Bereavement Transportation Services
                </h2>
                <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                  Thoughtful, coordinated transportation so your family can focus on what matters most.
                </p>
              </div>
              <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
                {services.map((service, index) => (
                  <Card key={index} className="border-border/50 bg-card hover:border-primary/30 transition-all duration-300 hover:scale-105 animate-fade-in" style={{ animationDelay: `${0.3 + index * 0.15}s`, animationFillMode: 'both' }}>
                    <CardContent className="p-8 text-center">
                      <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
                        <service.icon className="w-8 h-8 text-primary" />
                      </div>
                      <h3 className="text-xl font-serif font-bold text-foreground mb-4">
                        {service.title}
                      </h3>
                      <p className="text-muted-foreground leading-relaxed">
                        {service.description}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </section>

          {/* Transparent Pricing Section */}
          <section className="py-20 bg-gradient-to-b from-background via-muted/10 to-background">
            <div className="container mx-auto px-4">
              <div className="text-center mb-12 animate-fade-in" style={{ animationDelay: '0.2s', animationFillMode: 'both' }}>
                <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-4">
                  Transparent Bereavement Pricing
                </h2>
                <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                  Clear, compassionate pricing so you can plan with confidence during this difficult time.
                </p>
              </div>
              
              <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto mb-10">
                {/* Service A: The Committal Service */}
                <Card className="border-border/50 bg-gradient-to-b from-card to-muted/20 hover:border-primary/40 transition-all duration-300 hover:scale-[1.02] animate-fade-in relative overflow-hidden" style={{ animationDelay: '0.3s', animationFillMode: 'both' }}>
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-primary/50 to-transparent" />
                  <CardContent className="p-8 text-center">
                    <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-5">
                      <Heart className="w-7 h-7 text-primary" />
                    </div>
                    <h3 className="text-xl font-serif font-bold text-foreground mb-2">
                      The Committal Service
                    </h3>
                    <div className="text-3xl font-bold text-primary mb-1">
                      $324
                    </div>
                    <p className="text-xs text-muted-foreground/70 mb-4">
                      ($270 + 20% gratuity)
                    </p>
                    <p className="text-muted-foreground text-sm leading-relaxed mb-6">
                      2-hour minimum charter. Perfect for transport from a local home or church to the cemetery and back.
                    </p>
                    <ul className="text-left space-y-2 text-sm text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                        <span>Up to 2 hours of service</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                        <span>Home/church to cemetery</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                        <span>Return transportation</span>
                      </li>
                    </ul>
                  </CardContent>
                </Card>

                {/* Service B: The Morning Procession */}
                <Card className="border-primary/40 bg-gradient-to-b from-card to-primary/5 hover:border-primary/60 transition-all duration-300 hover:scale-[1.02] animate-fade-in relative overflow-hidden shadow-lg" style={{ animationDelay: '0.45s', animationFillMode: 'both' }}>
                  <div className="absolute top-0 left-0 right-0 h-1 bg-primary" />
                  <div className="absolute top-4 right-4">
                    <span className="text-xs font-semibold bg-primary/20 text-primary px-2 py-1 rounded-full">
                      POPULAR
                    </span>
                  </div>
                  <CardContent className="p-8 text-center">
                    <div className="w-14 h-14 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-5">
                      <Car className="w-7 h-7 text-primary" />
                    </div>
                    <h3 className="text-xl font-serif font-bold text-foreground mb-2">
                      The Morning Procession
                    </h3>
                    <div className="text-3xl font-bold text-primary mb-1">
                      $405
                    </div>
                    <p className="text-xs text-muted-foreground/70 mb-4">
                      ($337.50 + 20% gratuity)
                    </p>
                    <p className="text-muted-foreground text-sm leading-relaxed mb-6">
                      3-hour dedicated service plus wait time. Ideal for a full funeral service followed by a burial.
                    </p>
                    <ul className="text-left space-y-2 text-sm text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                        <span>Up to 3 hours of service</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                        <span>Wait time included</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                        <span>Procession driving</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                        <span>Full funeral + burial</span>
                      </li>
                    </ul>
                  </CardContent>
                </Card>

                {/* Service C: The Family Logistics */}
                <Card className="border-border/50 bg-gradient-to-b from-card to-muted/20 hover:border-primary/40 transition-all duration-300 hover:scale-[1.02] animate-fade-in relative overflow-hidden" style={{ animationDelay: '0.6s', animationFillMode: 'both' }}>
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-primary/50 to-transparent" />
                  <CardContent className="p-8 text-center">
                    <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-5">
                      <Plane className="w-7 h-7 text-primary" />
                    </div>
                    <h3 className="text-xl font-serif font-bold text-foreground mb-2">
                      The Family Logistics
                    </h3>
                    <div className="text-3xl font-bold text-primary mb-1">
                      $138
                    </div>
                    <p className="text-xs text-muted-foreground/70 mb-4">
                      ($115 + 20% gratuity)
                    </p>
                    <p className="text-muted-foreground text-sm leading-relaxed mb-6">
                      Local SAT Airport transfers for out-of-town relatives. Flat rate for local transfers.
                    </p>
                    <ul className="text-left space-y-2 text-sm text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                        <span>SAT Airport pickup/dropoff</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                        <span>Meet-and-greet service</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                        <span>Flat rate pricing</span>
                      </li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
              
              {/* Gratuity Note */}
              <p className="text-center text-sm text-muted-foreground max-w-2xl mx-auto mb-8 animate-fade-in" style={{ animationDelay: '0.75s', animationFillMode: 'both' }}>
                <em>
                  To simplify your day, a mandatory 20% gratuity is included in all final quotes, 
                  ensuring no cash handling is necessary during the service.
                </em>
              </p>
              
              {/* Custom Quote Button */}
              <div className="text-center animate-fade-in" style={{ animationDelay: '0.9s', animationFillMode: 'both' }}>
                <Button 
                  className="bg-primary hover:bg-primary/90 text-primary-foreground text-lg px-8 py-6 font-semibold shadow-lg transition-transform duration-300 hover:scale-105" 
                  asChild
                >
                  <Link to="/request-quote">
                    <Heart className="w-5 h-5 mr-2" />
                    Request a Custom Bereavement Quote
                  </Link>
                </Button>
              </div>
            </div>
          </section>

          <section className="py-20 bg-muted/20">
            <div className="container mx-auto px-4">
              <div className="text-center mb-12 animate-fade-in" style={{ animationDelay: '0.2s', animationFillMode: 'both' }}>
                <div className="flex justify-center mb-4">
                  <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
                    <MapPin className="w-7 h-7 text-primary" />
                  </div>
                </div>
                <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-4">
                  Serving San Antonio's Funeral Homes & Cemeteries
                </h2>
                <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                  We proudly provide dignified transportation services to families at these respected establishments.
                </p>
              </div>
              
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
                {[
                  { name: "Mission Park Funeral Chapels", locations: "Multiple Locations" },
                  { name: "Porter Loring Mortuary", locations: "North & Downtown" },
                  { name: "Sunset Memorial Park", locations: "I-35 North" },
                  { name: "San Fernando Cemetery", locations: "West Side" },
                  { name: "Fort Sam Houston National Cemetery", locations: "JBSA Access Required" },
                  { name: "Alamo Funeral Home", locations: "South San Antonio" },
                  { name: "Heritage Funeral Home", locations: "Castle Hills" },
                  { name: "Hillcrest Funeral Home", locations: "Uvalde Road" },
                  { name: "Funeral Caring USA", locations: "Multiple Locations" }
                ].map((home, index) => (
                  <Card key={index} className="border-border/50 bg-card hover:border-primary/30 transition-all duration-300 hover:scale-105 animate-fade-in" style={{ animationDelay: `${0.3 + index * 0.1}s`, animationFillMode: 'both' }}>
                    <CardContent className="p-6 flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground">{home.name}</h3>
                        <p className="text-sm text-muted-foreground">{home.locations}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
              
              <p className="text-center text-muted-foreground mt-10 max-w-2xl mx-auto">
                Don't see your funeral home listed? We serve <strong className="text-foreground">all funeral homes and cemeteries</strong> throughout 
                San Antonio, Bexar County, and the surrounding Hill Country. Contact us to arrange transportation.
              </p>
            </div>
          </section>

          {/* Testimonials Section - Centered */}
          <section className="py-20 bg-background">
            <div className="container mx-auto px-4">
              <div className="text-center mb-12 animate-fade-in" style={{ animationDelay: '0.2s', animationFillMode: 'both' }}>
                <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-4">
                  Families We've Had the Honor to Serve
                </h2>
                <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                  During life's most difficult moments, we strive to provide comfort through compassionate service.
                </p>
              </div>
              
              <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
                {/* Testimonial 1 */}
                <Card className="border-primary/20 bg-card/50 backdrop-blur shadow-xl animate-fade-in" style={{ animationDelay: '0.3s', animationFillMode: 'both' }}>
                  <CardContent className="p-8 md:p-10 text-center h-full flex flex-col">
                    <div className="flex justify-center mb-6">
                      <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
                        <Quote className="w-7 h-7 text-primary" />
                      </div>
                    </div>
                    <blockquote className="text-lg text-foreground leading-relaxed mb-6 italic flex-grow">
                      "During our father's funeral, Rick provided the most compassionate and professional service we could have asked for. 
                      He handled every detail with such care—from picking up family at the airport to ensuring we arrived at Fort Sam Houston 
                      National Cemetery without any stress. His military background brought an extra level of understanding and respect."
                    </blockquote>
                    <div className="border-t border-border/50 pt-6 mt-auto">
                      <p className="text-foreground font-semibold">— The Martinez Family</p>
                      <p className="text-muted-foreground text-sm mt-1">San Antonio, TX</p>
                      <div className="flex justify-center gap-1 mt-3">
                        {[...Array(5)].map((_, i) => (
                          <svg key={i} className="w-4 h-4 text-primary fill-current" viewBox="0 0 20 20">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Testimonial 2 */}
                <Card className="border-primary/20 bg-card/50 backdrop-blur shadow-xl animate-fade-in" style={{ animationDelay: '0.5s', animationFillMode: 'both' }}>
                  <CardContent className="p-8 md:p-10 text-center h-full flex flex-col">
                    <div className="flex justify-center mb-6">
                      <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
                        <Quote className="w-7 h-7 text-primary" />
                      </div>
                    </div>
                    <blockquote className="text-lg text-foreground leading-relaxed mb-6 italic flex-grow">
                      "When my mother passed, the last thing I wanted to worry about was transportation. Rick took care of everything—
                      coordinating airport pickups for relatives flying in from California, and driving our family in the procession. 
                      His quiet professionalism and genuine empathy meant the world to us. I cannot recommend him enough."
                    </blockquote>
                    <div className="border-t border-border/50 pt-6 mt-auto">
                      <p className="text-foreground font-semibold">— David & Susan Chen</p>
                      <p className="text-muted-foreground text-sm mt-1">Stone Oak, San Antonio</p>
                      <div className="flex justify-center gap-1 mt-3">
                        {[...Array(5)].map((_, i) => (
                          <svg key={i} className="w-4 h-4 text-primary fill-current" viewBox="0 0 20 20">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-16 bg-background">
            <div className="container mx-auto px-4">
              <Card className="max-w-3xl mx-auto border-primary/20 bg-card animate-fade-in" style={{ animationDelay: '0.2s', animationFillMode: 'both' }}>
                <CardContent className="p-10 text-center">
                  <h2 className="text-3xl font-serif font-bold text-foreground mb-4">
                    We're Here to Help
                  </h2>
                  <p className="text-muted-foreground text-lg mb-8 max-w-xl mx-auto">
                    During this difficult time, let us handle the transportation details. 
                    Contact us for compassionate, professional service.
                  </p>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                    <Button 
                      className="bg-primary hover:bg-primary/90 text-primary-foreground text-lg px-8 py-6 w-full sm:w-auto font-semibold transition-transform duration-300 hover:scale-105"
                      asChild
                    >
                      <Link to="/contact">
                        <Heart className="w-5 h-5 mr-2" />
                        Request a Bereavement Quote
                      </Link>
                    </Button>
                    <Button 
                      variant="outline"
                      className="text-lg px-8 py-6 w-full sm:w-auto transition-transform duration-300 hover:scale-105"
                      asChild
                    >
                      <a href="tel:+12102967409">
                        <Phone className="w-5 h-5 mr-2" />
                        210-296-7409
                      </a>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="py-20 bg-muted/30">
            <div className="container mx-auto px-4">
              <div className="max-w-3xl mx-auto">
                <div className="text-center mb-12 animate-fade-in" style={{ animationDelay: '0.2s', animationFillMode: 'both' }}>
                  <div className="flex justify-center mb-4">
                    <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
                      <HelpCircle className="w-7 h-7 text-primary" />
                    </div>
                  </div>
                  <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-4">
                    Frequently Asked Questions
                  </h2>
                  <p className="text-muted-foreground text-lg">
                    Answers to common questions about our bereavement transportation services.
                  </p>
                </div>
                
                <Accordion type="single" collapsible className="space-y-4">
                  {faqItems.map((item, index) => (
                    <AccordionItem 
                      key={index} 
                      value={`item-${index}`}
                      className="bg-card border border-border/50 rounded-lg px-6 shadow-sm animate-fade-in"
                      style={{ animationDelay: `${0.3 + index * 0.1}s`, animationFillMode: 'both' }}
                    >
                      <AccordionTrigger className="text-left text-foreground font-medium py-5 hover:no-underline">
                        {item.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-muted-foreground pb-5 leading-relaxed">
                        {item.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </div>
          </section>

          {/* SEO Content Block */}
          <section className="py-20 bg-muted/20">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto prose prose-lg animate-fade-in" style={{ animationDelay: '0.2s', animationFillMode: 'both' }}>
                <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-8 text-center">
                  Why Choose Private Car Service Over a Stretch Limo?
                </h2>
                <div className="text-muted-foreground space-y-6 text-lg leading-relaxed">
                  <p>
                    When grieving, many families prefer the intimacy of a private luxury SUV over the spectacle 
                    of a stretch limousine. Our black-on-black Audi Q7 offers a low-profile, respectful presence. 
                    We serve all major San Antonio funeral homes and cemeteries, including Mission Park, Porter Loring, 
                    and Sunset Memorial Park.
                  </p>
                  <p>
                    As a <strong className="text-foreground">veteran-owned business</strong> with over 22 years of 
                    military service, owner Rick Ruiz-Estrada understands the unique needs of military families. 
                    Our authorized JBSA base access means we can transport families directly to Fort Sam Houston 
                    National Cemetery without the delays and complications that other transportation providers face.
                  </p>
                  <p>
                    Whether you need transportation for the immediate family, airport pickups for out-of-town relatives, 
                    or a professional driver for the funeral procession, Rick's Black Car Service provides 
                    <strong className="text-foreground"> dignified, private bereavement transportation</strong> throughout 
                    San Antonio and the surrounding Texas Hill Country.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Instant Quote CTA */}
          <InstantQuoteCTA />

        </main>
        <Footer />
      </div>
    </>
  );
};

export default FuneralTransportation;
